/** Row height in px for each layout `h` unit on the 12-column grid. */
export const GRID_ROW_HEIGHT = 260

/** Breakpoint from chart grid container width (not viewport). */
export function getGridBreakpoint(containerWidth) {
  const w = containerWidth || 0
  if (w < 480) return 'sm'
  if (w < 768) return 'md'
  return 'lg'
}

/**
 * Breakpoint with hysteresis so layout changes near 480/768px do not
 * ping-pong ResizeObserver → grid relayout → width change (freezes the tab).
 */
export function getGridBreakpointWithHysteresis(containerWidth, previous = 'lg') {
  const w = containerWidth || 0
  const prev = previous || 'lg'

  if (prev === 'lg') {
    if (w < 720) return w < 460 ? 'sm' : 'md'
    return 'lg'
  }
  if (prev === 'md') {
    if (w < 460) return 'sm'
    if (w >= 800) return 'lg'
    return 'md'
  }
  // sm
  if (w >= 800) return 'lg'
  if (w >= 500) return 'md'
  return 'sm'
}

export function isHorizontalBarWidget(w) {
  return !!(w && w.type === 'bar' && w.orientation === 'horizontal')
}

export function layoutSortOrder(w) {
  const l = w.layout || { x: 0, y: 0 }
  return (Number(l.y) || 0) * 1000 + (Number(l.x) || 0)
}

export function normalizeLayoutRect(layout) {
  const l = layout || { x: 0, y: 0, w: 12, h: 1 }
  const x = Math.max(0, Math.min(11, Number(l.x) || 0))
  const w = Math.max(1, Math.min(12, Number(l.w) || 12))
  return {
    x: Math.min(x, 12 - w),
    y: Math.max(0, Number(l.y) || 0),
    w,
    h: Math.max(1, Number(l.h) || 1),
  }
}

export function layoutsCollide(a, b) {
  if (a.x + a.w <= b.x) return false
  if (b.x + b.w <= a.x) return false
  if (a.y + a.h <= b.y) return false
  if (b.y + b.h <= a.y) return false
  return true
}

function sortLayoutIndices(layouts) {
  return layouts.map((_, i) => i).sort((a, b) => {
    const la = layouts[a]
    const lb = layouts[b]
    if (la.y !== lb.y) return la.y - lb.y
    return la.x - lb.x
  })
}

/** Move each widget up as far as possible without overlapping others. */
function compactLayoutsUpward(layouts) {
  const order = sortLayoutIndices(layouts)
  let anyChanged = false

  for (let pos = 0; pos < order.length; pos++) {
    const i = order[pos]
    const cur = { ...layouts[i] }
    while (cur.y > 0) {
      const candidate = { ...cur, y: cur.y - 1 }
      let blocked = false
      for (let j = 0; j < layouts.length; j++) {
        if (j === i) continue
        if (layoutsCollide(candidate, layouts[j])) {
          blocked = true
          break
        }
      }
      if (blocked) break
      cur.y = candidate.y
      anyChanged = true
    }
    layouts[i] = cur
  }

  return anyChanged
}

/** Move each widget left as far as possible without overlapping others. */
function compactLayoutsLeftward(layouts) {
  const order = sortLayoutIndices(layouts)
  let anyChanged = false

  for (let pos = 0; pos < order.length; pos++) {
    const i = order[pos]
    const cur = { ...layouts[i] }
    while (cur.x > 0) {
      const candidate = { ...cur, x: cur.x - 1 }
      let blocked = false
      for (let j = 0; j < layouts.length; j++) {
        if (j === i) continue
        if (layoutsCollide(candidate, layouts[j])) {
          blocked = true
          break
        }
      }
      if (blocked) break
      cur.x = candidate.x
      anyChanged = true
    }
    layouts[i] = cur
  }

  return anyChanged
}

/** Push overlapping widgets downward (e.g. after height increases). */
function resolveDownwardCollisions(layouts) {
  const order = sortLayoutIndices(layouts)
  let anyChanged = false

  for (let pos = 0; pos < order.length; pos++) {
    const i = order[pos]
    const cur = { ...layouts[i] }
    let moved = true
    while (moved) {
      moved = false
      for (let prev = 0; prev < pos; prev++) {
        const j = order[prev]
        const other = layouts[j]
        if (layoutsCollide(cur, other)) {
          const nextY = other.y + other.h
          if (cur.y < nextY) {
            cur.y = nextY
            moved = true
            anyChanged = true
          }
        }
      }
    }
    layouts[i] = cur
  }

  return anyChanged
}

/**
 * Resolve grid overlaps and compact empty rows when widgets shrink or grow.
 * Mutates widget.layout in place. Returns true if any layout changed.
 */
export function reflowWidgetLayouts(widgets) {
  if (!Array.isArray(widgets) || !widgets.length) return false
  if (widgets.length === 1) {
    const l = normalizeLayoutRect(widgets[0].layout)
    if (l.y === 0) return false
    widgets[0].layout = { ...l, y: 0 }
    return true
  }

  const layouts = widgets.map(w => normalizeLayoutRect(w.layout))
  let anyChanged = false

  // Expand: push colliding widgets down first.
  if (resolveDownwardCollisions(layouts)) anyChanged = true
  // Shrink: pull widgets back up / left into freed grid space.
  if (compactLayoutsUpward(layouts)) anyChanged = true
  if (compactLayoutsLeftward(layouts)) anyChanged = true
  // One more pass in case compaction created new overlaps (rare).
  if (resolveDownwardCollisions(layouts)) anyChanged = true

  if (anyChanged) {
    widgets.forEach((w, i) => {
      w.layout = { ...layouts[i] }
    })
  }
  return anyChanged
}

/**
 * CSS grid placement from config.json layout (x, y, w, h).
 * On narrow containers only, stack full-width in layout order.
 */
export function cellStyleForWidget(w, breakpoint) {
  const l = w.layout || { x: 0, y: 0, w: 12, h: 1 }
  const x = Number(l.x) || 0
  const y = Number(l.y) || 0
  const wSpan = Math.max(1, Math.min(12, Number(l.w) || 12))
  const hSpan = Math.max(1, Number(l.h) || 1)
  const order = layoutSortOrder(w)

  if (breakpoint !== 'lg') {
    return {
      gridColumn: '1 / -1',
      gridRow: 'auto',
      order,
      minHeight: `${hSpan * GRID_ROW_HEIGHT}px`,
    }
  }

  return {
    gridColumn: `${x + 1} / span ${wSpan}`,
    gridRow: `${y + 1} / span ${hSpan}`,
    order,
    minHeight: 0,
    height: '100%',
  }
}

export function isCompactChart(breakpoint) {
  return breakpoint !== 'lg'
}
